import { loadEcsTreatments } from '@ms/yammer-data/dist/domains/ecs/actions';
import { addEtagToAnalyticsEvent } from '@ms/yammer-data/dist/domains/ecs/addEtagToAnalyticsEvent';
import { addEtagToAnalyticsV2Event } from '@ms/yammer-data/dist/domains/ecs/addEtagToAnalyticsV2Event';
import { convertLegacyId } from '@ms/yammer-data/dist/domains/legacyId/actions';
import { isIdConversionRequested } from '@ms/yammer-data/dist/domains/legacyId/selectors';
import { setNetworkContext } from '@ms/yammer-data/dist/domains/networkContext/actions';
import { loadNetworkTopicSettings } from '@ms/yammer-data/dist/domains/networkTopicSettings/actions';
import { shouldFetchNetworkTopicSettings } from '@ms/yammer-data/dist/domains/networkTopicSettings/selectors';
import { setPersistKey } from '@ms/yammer-data/dist/domains/persistConfig/actions';
import { getRawUserLocaleSetting } from '@ms/yammer-data/dist/domains/session/selectors';
import { configure } from '@ms/yammer-data/dist/domains/sessionConfigure/actions';
import { loadCurrentUser } from '@ms/yammer-data/dist/domains/sessionLoadCurrentUser/actions';
import { setTelemetryUserContext } from '@ms/yammer-data/dist/domains/telemetry/actions';
import type { ThunkDispatch } from '@ms/yammer-data/dist/types';
import { setLocale } from '@ms/yammer-i18n-react';
import {
  isDogfoodPerfHUDPersistEnabled,
  loadLogEventInDogfoodPerfHUDStoreMiddleware,
} from '@ms/yammer-page-route-telemetry';
import type { TelemetryMiddleware } from '@ms/yammer-telemetry';
import { addMiddleware, reportInfo } from '@ms/yammer-telemetry';
import type { PopupAuthenticator } from '@ms/yammer-web-authenticators';
import { checkThirdPartyCookieSupport, reportThirdPartyCookieEvent } from '@ms/yammer-web-support/dist/cookies';
import { writeHostingCookies } from '@ms/yammer-web-support/dist/hosting';
import { getNetworkContext, getQueryParameter } from '@ms/yammer-web-support/dist/location';

import type { YammerEmbedState } from '../../../state/types';
import config from '../../config';
import { persistConfig } from '../../persistedState';

import { reportErrorWithCode } from './bootstrapTelemetry';

export type ActionDispatcher = (dispatch: ThunkDispatch<YammerEmbedState>) => Promise<void>;

export const loadUserData: ActionDispatcher = async (dispatch) => {
  try {
    await dispatch(loadCurrentUser({ initiator: 'bootstrap' }));
    await dispatch(setTelemetryUserContext());
  } catch (err) {
    reportErrorWithCode(err, 'LoadCurrentUserFailed');
    throw err;
  }
};

type SetupFeatureFlighting = (
  dispatch: ThunkDispatch<YammerEmbedState>,
  getState: () => YammerEmbedState
) => Promise<void>;
export const setupFeatureFlighting: SetupFeatureFlighting = async (dispatch) => {
  try {
    await dispatch(loadEcsTreatments({ persistKey: persistConfig.key, initiator: 'bootstrap' }));
  } catch (err) {
    reportErrorWithCode(err, 'InitializeFeatureFlightingFailed');
  }
};

export const ensureTokensAcquired: (authenticator: PopupAuthenticator) => Promise<void> = async (authenticator) => {
  const yammerToken = await authenticator.acquireToken([config.adal.resources.yammer]);
  try {
    reportThirdPartyCookieEvent(yammerToken);
    writeHostingCookies({ yammerToken, shouldSetSameSite: true, partitioned: !checkThirdPartyCookieSupport() });
  } catch (error) {
    reportErrorWithCode(error, 'WriteHostingCookiesFailed');
    throw error;
  }
};

export const configureSession: (dispatch: ThunkDispatch<YammerEmbedState>) => void = (dispatch) => {
  dispatch(configure(config));
};

export const configureNetworkContext: (dispatch: ThunkDispatch<YammerEmbedState>) => void = (dispatch) => {
  const networkContext = getNetworkContext(window.location.pathname);
  dispatch(setNetworkContext(networkContext));
};

export const savePersistKey: (dispatch: ThunkDispatch<YammerEmbedState>) => void = (dispatch) => {
  dispatch(setPersistKey(persistConfig.key));
};

type GetAddEtagMiddleware = (getState: () => YammerEmbedState) => TelemetryMiddleware;
const getAddEtagToAnalyticsEventMiddleware: GetAddEtagMiddleware = (getState) => (event) =>
  addEtagToAnalyticsEvent(event, getState);

type GetAddV2EtagMiddleware = (getState: () => YammerEmbedState) => TelemetryMiddleware;
const getAddV2EtagToAnalyticsEventMiddleware: GetAddV2EtagMiddleware = (getState) => (event) =>
  addEtagToAnalyticsV2Event(event, getState);

export const addTelemetryMiddleware: (getState: () => YammerEmbedState) => Promise<void> = async (getState) => {
  const etagMiddleware = getAddEtagToAnalyticsEventMiddleware(getState);
  const v2EtagMiddleware = getAddV2EtagToAnalyticsEventMiddleware(getState);
  addMiddleware(etagMiddleware);
  addMiddleware(v2EtagMiddleware);

  const isPerfHUDEnabled = await isDogfoodPerfHUDPersistEnabled(persistConfig.key);
  if (isPerfHUDEnabled) {
    const { logEventInDogfoodPerfHUDStoreMiddleware } = await loadLogEventInDogfoodPerfHUDStoreMiddleware();
    addMiddleware(logEventInDogfoodPerfHUDStoreMiddleware);
  }
};

const getDefaultLocale = (): string => {
  const browserLocale = window.navigator.language;
  const defaultLocale = 'en-US';

  return browserLocale || defaultLocale;
};

export const setLocaleFromUserData: (state: YammerEmbedState) => void = (state) => {
  const defaultLocale = getDefaultLocale();
  const currentLocale = getRawUserLocaleSetting(state) || defaultLocale;
  setLocale(currentLocale);
};

type ConvertDefaultGroupLegacyId = (dispatch: ThunkDispatch<YammerEmbedState>, state: YammerEmbedState) => void;
export const convertDefaultGroupLegacyId: ConvertDefaultGroupLegacyId = (dispatch, state) => {
  const defaultGroupId = getQueryParameter('defaultGroupId') as string;
  if (!defaultGroupId) {
    return;
  }

  if (!isIdConversionRequested(state, defaultGroupId, 'GROUP')) {
    void dispatch(convertLegacyId(defaultGroupId, 'GROUP', 'bootstrap_async'));
  }
};

type LoadBootstrapNetworkTopicSettings = (
  dispatch: ThunkDispatch<YammerEmbedState>,
  state: YammerEmbedState
) => Promise<void>;
export const loadBootstrapNetworkTopicSettings: LoadBootstrapNetworkTopicSettings = async (dispatch, state) => {
  if (!shouldFetchNetworkTopicSettings(state)) {
    return;
  }

  try {
    await dispatch(loadNetworkTopicSettings({ initiator: 'bootstrap_async' }));
  } catch (error) {
    reportErrorWithCode(error, 'NetworkTopicSettingsFailed');
  }
};

export const reportYamJSRedirect = (): void => {
  const code = getQueryParameter('code');

  if (code) {
    const codeVal = Array.isArray(code) ? code.join(',') : code;

    reportInfo({ eventName: 'yamjs_eol', eventProperties: { message: `yamjs-embed/${codeVal}` } });
  }
};
